import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { fieldService, attributeService, relationshipService, referenceService } from '../services';
import {
  AttributeSensitivity,
  formatDatasetSubtitle,
  formatDatasetPath,
  formatData,
  handleError,
  Role,
  Asset,
  technicalDefinitionLanguage,
} from '../helpers';
import {
  Changelog,
  DataTable,
  Icon,
  MainColumn,
  ObjectDetail,
  ObjectDetails,
  SkeletonLoader,
  Tabs,
  TechnicalDefinition,
  TextareaCode,
  TextareaReadOnly,
  textareaInitialState,
  textareaCodeInitialState,
} from '../components';
import {
  useCustomProperties,
  useSideDialog,
  useModalDialog,
  useQueryParams,
  useGlobalState,
  useNotFound,
  useMessage,
} from '../hooks';

export const Field = ({ abortFetch }) => {
  const { queryParams } = useQueryParams();

  const [field, setField] = useState([]);
  const [attribute, setAttribute] = useState({});
  const [fromReferences, setFromReferences] = useState([]);
  const [relationships, setRelationships] = useState([]);
  const [loggedInUser, setLoggedInUser] = useGlobalState('loggedInUser');
  const [currentDatasetAncestors] = useGlobalState('currentDatasetAncestors');
  const [loading, setLoading] = useState(true);
  const [loadingTable, setLoadingTable] = useState(true);
  const [defaultSelectedTab, setDefaultSelectedTab] = useState({ tab: queryParams.tab });

  // Which tab opens by default. A field that carries a technical definition leads with it, since
  // that is what the reader came for; every other field opens on Relationships. This has to wait
  // for the field to load: Tabs picks its default from the first child, and on the first render
  // the technical definition tab does not exist yet, so leaving it to Tabs would always land on
  // Relationships. Re-run only when that presence changes, so a tab the user clicks afterwards is
  // left alone. An explicit ?tab= wins, unless it names the tab this field does not have.
  useEffect(() => {
    const hasTechnicalDefinition = !!field?.field_technical_definition;
    const requested = queryParams.tab;
    const requestedIsAvailable = requested !== 'technical_definition' || hasTechnicalDefinition;

    setDefaultSelectedTab({
      tab:
        requested && requestedIsAvailable
          ? requested
          : hasTechnicalDefinition
            ? 'technical_definition'
            : 'relationships',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field?.field_technical_definition]);

  const { system_id, datasource_id, dataset_group_id, dataset_id, field_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { showMessage } = useMessage();
  const { showSideDialog, hideSideDialog, setSideDialog } = useSideDialog();
  const { showModalDialog, hideModalDialog } = useModalDialog();
  const { notFound, setNotFound, NotFound } = useNotFound();
  const { renderCustomPropertyValues, fetchCustomProperties } = useCustomProperties({
    objectName: Asset.Field,
    objectIds: [field_id],
  });

  useEffect(() => {
    fetchItems();
    fetchRelationships();
  }, [field_id]);

  const fetchFields = async () => {
    await fieldService
      .getById(field_id, abortFetch)
      .then(res => {
        if (res.fields.length < 1) {
          setNotFound(true);
        } else {
          setField(res.fields[0]);
          if (res.fields[0].attribute_id) {
            fetchFromReferences(res.fields[0].attribute_id);
          }
        }
      })
      .catch(err => {
        handleError({ err, showMessage, setNotFound });
      });
  };

  const fetchAttributes = async () => {
    await attributeService
      .getByFieldId(field_id, abortFetch)
      .then(res => {
        res && setAttribute(res.attributes[0]);
      })
      .catch(err => {
        handleError({ err, showMessage });
      });
  };

  const fetchItems = () => {
    Promise.all([fetchFields(), fetchAttributes()])
      .catch(err => {
        handleError({ err, showMessage });
      })
      .finally(res => setLoading(false));
  };

  const fetchFromReferences = async attributeId => {
    await referenceService
      .getByFromId(Asset.Attribute, attributeId, abortFetch)
      .then(res => {
        res && setFromReferences(res.references);
      })
      .catch(err => {
        handleError({ err, showMessage });
      });
  };

  const fetchRelationships = async () => {
    setLoadingTable(true);
    await relationshipService
      .getByObjectId(Asset.Field, field_id, abortFetch)
      .then(result => {
        result && setRelationships(formatRelationships(result.relationships));
        setLoadingTable(false);
      })
      .catch(err => {
        handleError({ err, showMessage }, () => {
          setLoadingTable(false);
        });
      });
  };

  const formatRelationships = data => {
    return data.map(item => {
      if (parseInt(field_id) === item.from_field_id) {
        return {
          relationship_type: item.relationship_type,
          direction_icon: 'arrow_right',
          direction: item.from_field_name + ' references the related field',
          dataset_name: item.from_dataset_name,
          datatype_category: item.from_datatype_category,
          datatype_fullname: item.from_datatype_fullname,
          field_name: item.from_field_name,
          dataset_description: item.from_dataset_description,
          datatype_name: item.from_datatype_name,
          attribute_description: item.from_attribute_description,
          field_id: item.from_field_id,
          dataset_id: item.from_dataset_id,
          dataset_group_id: item.from_dataset_group_id,
          datasource_id: item.from_datasource_id,
          system_id: item.from_system_id,
          related_dataset_name: item.to_dataset_name,
          related_dataset_type_name: item.to_dataset_type_name,
          related_datatype_category: item.to_datatype_category,
          related_datatype_fullname: item.to_datatype_fullname,
          related_field_name: item.to_field_name,
          related_dataset_description: item.to_dataset_description,
          related_datatype_name: item.to_datatype_name,
          related_attribute_description: item.to_attribute_description,
          related_dataset_group_name: item.to_dataset_group_name,
          related_datasource_name: item.to_datasource_name,
          related_system_name: item.to_system_name,
          related_field_id: item.to_field_id,
          related_dataset_id: item.to_dataset_id,
          related_dataset_group_id: item.to_dataset_group_id,
          related_datasource_id: item.to_datasource_id,
          related_system_id: item.to_system_id,
          related_dataset_path: formatDatasetPath(
            {
              dataset_id: item.to_dataset_id,
              dataset_name: item.to_dataset_name,
              datasource_type_category: item.to_datasource_type_category,
              dataset_type_code: item.to_dataset_type_code,
              dataset_source_url: item.to_dataset_source_url,
              dataset_subtype_name: item.to_dataset_subtype_name,
              dataset_subtype_operation: item.to_dataset_subtype_operation,
              system_id: item.to_system_id,
              datasource_id: item.to_datasource_id,
              dataset_group_id: item.to_dataset_group_id,
            },
            { ancestors: item.to_dataset_ancestors }
          ),
        };
      } else {
        return {
          relationship_type: item.relationship_type,
          direction_icon: 'arrow_left',
          direction: 'The related field references ' + item.to_field_name,
          dataset_name: item.to_dataset_name,
          datatype_category: item.to_datatype_category,
          datatype_fullname: item.to_datatype_fullname,
          field_name: item.to_field_name,
          dataset_description: item.to_dataset_description,
          datatype_name: item.to_datatype_name,
          attribute_description: item.to_attribute_description,
          field_id: item.to_field_id,
          dataset_id: item.to_dataset_id,
          dataset_group_id: item.to_dataset_group_id,
          datasource_id: item.to_datasource_id,
          system_id: item.to_system_id,
          related_dataset_name: item.from_dataset_name,
          related_dataset_type_name: item.from_dataset_type_name,
          related_datatype_category: item.from_datatype_category,
          related_datatype_fullname: item.from_datatype_fullname,
          related_field_name: item.from_field_name,
          related_dataset_description: item.from_dataset_description,
          related_datatype_name: item.from_datatype_name,
          related_attribute_description: item.from_attribute_description,
          related_dataset_group_name: item.from_dataset_group_name,
          related_datasource_name: item.from_datasource_name,
          related_system_name: item.from_system_name,
          related_field_id: item.from_field_id,
          related_dataset_id: item.from_dataset_id,
          related_dataset_group_id: item.from_dataset_group_id,
          related_datasource_id: item.from_datasource_id,
          related_system_id: item.from_system_id,
          related_dataset_path: formatDatasetPath(
            {
              dataset_id: item.from_dataset_id,
              dataset_name: item.from_dataset_name,
              datasource_type_category: item.from_datasource_type_category,
              dataset_type_code: item.from_dataset_type_code,
              dataset_source_url: item.from_dataset_source_url,
              dataset_subtype_name: item.from_dataset_subtype_name,
              dataset_subtype_operation: item.from_dataset_subtype_operation,
              system_id: item.from_system_id,
              datasource_id: item.from_datasource_id,
              dataset_group_id: item.from_dataset_group_id,
            },
            { ancestors: item.from_dataset_ancestors }
          ),
        };
      }
    });
  };

  const editField = async data => {
    const fields = { fields: Array.isArray(data) ? data : [data] };

    await fieldService
      .update(fields)
      .then(async res => {
        fetchItems();
        fetchCustomProperties(false);
        showMessage(res.message);
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const deleteField = async data => {
    await fieldService
      .destroy({ fields: data })
      .then(async res => {
        showMessage(res.message);
        hideModalDialog();

        if (location.key !== 'default') {
          // Arrived here by navigating inside Katalogue, so return the user where they came from.
          navigate(-1);
        } else {
          // Landed directly on this page — a deep link, a refresh or a new tab — so there is no
          // history to return to. Fall back to the deleted asset's parent.
          navigate(`/systems/${system_id}/${datasource_id}/${dataset_group_id}/${dataset_id}`);
        }
      })
      .catch(err => {
        handleError({ err, showMessage });
      });
  };

  const getKeyIcon = () => {
    if (
      data.is_primary_key ||
      relationships.find(
        x =>
          x.field_id === data.field_id &&
          x.relationship_type !== 'virtual' &&
          x.direction_icon === 'arrow_left'
      )
    ) {
      return <Icon name="primary key" tooltip="primary key" />;
    } else if (relationships.length > 0) {
      return <Icon name="foreign key" tooltip="foreign key" />;
    } else {
      return null;
    }
  };

  const data = loading ? [] : field;

  const editable =
    loggedInUser &&
    (loggedInUser.user_role_name === Role.admin || loggedInUser.user_role_name === Role.editor);

  if (notFound) {
    return <NotFound />;
  }

  return (
    <div className="columns narrow-margin">
      <MainColumn>
        <ObjectDetails
          type="Field"
          title={
            !loading && (
              <span>
                {data.field_name}{' '}
                <span className="light-text">
                  {!attribute ? '' : attribute.attribute_unit_code}
                </span>
              </span>
            )
          }
          statusId={data.status_id}
          subtitleIcon={[
            getKeyIcon(),
            <Icon name={data.datatype_category} tooltip={data.datatype_fullname} />,
          ]}
          subtitleText={
            data &&
            !loading &&
            formatDatasetSubtitle(data, {
              ancestors: currentDatasetAncestors?.ancestors,
              trailingParts: [data.field_name],
            })
          }
          description={data.field_description}
          fromReferences={fromReferences}
          loading={loading}
          details={[
            {
              title: (
                <React.Fragment>
                  <span>{`Source Description`}</span>
                  <span className="second-row">{`${data.secondary_connector_name}`}</span>
                </React.Fragment>
              ),
              data: data.field_source_description_secondary,
            },
            {
              title: (
                <React.Fragment>
                  <span>{`Source Description`}</span>
                  <span className="second-row">{`${data.connector_name}`}</span>
                </React.Fragment>
              ),
              data: data.field_source_description,
            },
            { title: 'Source Id', data: data.field_source_id },
            { title: 'Datatype', data: data.datatype_fullname },
            {
              title: 'Default Value',
              data: data.default_value && (
                <TextareaCode
                  name="code-editor"
                  value={textareaCodeInitialState({ value: data.default_value })}
                  readOnly={true}
                  showLineNumbers={false}
                />
              ),
            },
            {
              title: 'Is Nullable',
              data: data.is_nullable === true ? 'Yes' : data.is_nullable === false ? 'No' : null,
            },
            { title: 'Ordinal Position', data: data.ordinal_position },
            ...renderCustomPropertyValues().details,
            { title: 'Id', data: data.field_id, tooltip: 'Katalogue-internal id for this asset' },
          ]}
          editable={editable}
          buttons={[
            {
              buttonStyle: 'main-button',
              value: 'Edit',
              onClick: () => showSideDialog('editField', [data], editField),
            },
            {
              value: 'Delete',
              onClick: () => showModalDialog('deleteField', [data], deleteField),
            },
          ]}
        >
          {attribute?.attribute_definition && (
            <ObjectDetail
              label="Definition"
              loading={loading}
              showLoadingForChildren={true}
              loadingChildrenCount={2}
            >
              <TextareaReadOnly
                value={textareaInitialState({
                  value: attribute.attribute_definition,
                  readOnly: true,
                  descriptions: fromReferences,
                })}
              />
            </ObjectDetail>
          )}

          {data?.attribute_sensitivity_name && (
            <ObjectDetail label="Data Sensitivity" loading={loading} showLoadingForChildren={true}>
              <React.Fragment>
                {data.is_pii && (
                  <Icon
                    name="pii"
                    tooltip="Classified as Personally Identifiable Information (PII)"
                  />
                )}
                <Icon
                  name="attribute_sensitivity"
                  label={data.attribute_sensitivity_name}
                  value={data.attribute_sensitivity_level}
                  values={AttributeSensitivity}
                />
              </React.Fragment>
            </ObjectDetail>
          )}

          {data?.attribute_id && (
            <ObjectDetail
              label="Associated Attribute"
              loading={loading}
              showLoadingForChildren={true}
            >
              <Link to={'/attributes/' + data.attribute_id} className="link">
                <Icon
                  name={attribute.attribute_role_name}
                  tooltip={attribute.attribute_role_name}
                />
                {data.attribute_name}
              </Link>
            </ObjectDetail>
          )}

          {renderCustomPropertyValues().main}
        </ObjectDetails>
      </MainColumn>

      <div className="column">
        <Tabs
          className="slim left"
          //onTabChange={ onTabChange }
          defaultSelectedTab={defaultSelectedTab}
          disableTabsWithoutResults={true}
        >
          {/* Only for a field that has one, and first in the list: Tabs filters out falsy
              children and defaults to the first, so a field without a technical definition
              opens on Relationships instead. */}
          {data.field_technical_definition && (
            <div label="Technical Definition" tabid="technical_definition">
              <TechnicalDefinition
                data={data}
                definition={data.field_technical_definition}
                showInlineToolbar={true}
                language={technicalDefinitionLanguage(data.datasource_type_code, Asset.Field)}
              />
            </div>
          )}

          <div label="Relationships" tabid="relationships">
            <DataTable
              columns={[
                { id: 'direction_icon', name: '', type: 'icon', tooltip: 'direction' },
                {
                  id: 'related_datatype_category',
                  name: '',
                  type: 'icon',
                  tooltip: 'related_datatype_fullname',
                },
                {
                  id: 'related_field_name',
                  name: 'Related Field',
                  link: '/systems/:related_system_id/:related_datasource_id/:related_dataset_group_id/:related_dataset_id/:related_field_id',
                  tooltip: 'related_attribute_description',
                },
                {
                  id: 'related_dataset_type_name',
                  name: '',
                  type: 'icon',
                  tooltip: 'related_dataset_type_name',
                },
                {
                  // related_dataset_path holds the formatDatasetPath() output (its segments link
                  // themselves); sort still keys off related_dataset_name.
                  id: 'related_dataset_path',
                  name: 'Related Dataset',
                  sortField: 'related_dataset_name',
                },
                {
                  id: 'related_dataset_group_name',
                  name: 'Related Dataset Group',
                  link: '/systems/:related_system_id/:related_datasource_id/:related_dataset_group_id',
                },
                {
                  id: 'related_datasource_name',
                  name: 'Related Datasource',
                  link: '/systems/:related_system_id/:related_datasource_id',
                },
                {
                  id: 'related_system_name',
                  name: 'Related System',
                  link: '/systems/:related_system_id',
                },
                { id: 'relationship_type', name: 'Relationship Type', className: 'small-text' },
              ]}
              data={relationships}
              idColumn="related_field_id"
              editable={false}
              loading={loadingTable}
              filterable={true}
              filterObjects={[
                'related_field_name',
                'related_dataset_name',
                'related_dataset_group_name',
                'related_datasource_name',
                'related_system_name',
                'relationship_type',
              ]}
            />
          </div>

          <div label="Changelog" tabid="changelog">
            <Changelog
              objectName={Asset.Field}
              objectId={field_id}
              filterObjects={[
                'changedData.new_data||old_data.field_name',
                'changedData.new_data||old_data.attribute_name',
                'changedData.new_data||old_data.datatype_name',
                'changedData.new_data||old_data.dataset_name',
                'changedData.new_data||old_data.field_technical_definition',
                'changedData.new_data||old_data.is_primary_key',
                'changedData.property',
                'changed_by_user_username',
                'is_service_account_user',
              ]}
              filterObjectsValueMap={{
                is_service_account_user: {
                  true: { label: 'Datasource Sync' },
                  false: { label: '' },
                },
                is_primary_key: { true: { label: 'Primary Key' }, false: { label: '' } },
              }}
            />
          </div>
        </Tabs>
      </div>
    </div>
  );
};
